import React, { useEffect, useMemo, useState } from 'react';
import './address-form.scss';
import { Form, InputType } from '@ampol/design-system';
import { string } from 'yup';
import {
  addressTypeTitle,
  salutations,
  addressValidationSchema,
  AUSSIE_STATES,
} from '../../utilities/constants';
import Regex from 'constants/regex';
import Validation from 'constants/validation-messages';
import yup from 'utilities/custom-yup-validation';

/**
 * Address form for entering an Address
 * @param title Title/Address type label
 * @param addressType Type code of address, defines what fields are available
 * @param address Address properties to update
 * @param onSubmit Form submit event
 * @param key Form unique id
 * @param onStateChange Form state change event
 * @param readOnly Is form in read only mode
 * @returns JSX Element
 */
const AddressForm = ({ title, addressType, address, onSubmit, formId, onStateChange, readOnly }) => {
  const suppressTypeCode = 'SUPPRESS';

  const hasDeliveryType = {
    'CRD_ML_ADDR': false,
    'AI_ADDR': true,
    'STMT_ADDR': true,
    'PHS_ADDR': false,
    'PST_ADDR': false,
    'IR_ADDR': true,
    'CI_ADDR': true,
    'SC_ADDR': true,
  };

  const deliveryTypes = [
    { label: 'Mail', value: 'MAIL' },
    { label: 'Email', value: 'EMAIL' },
    { label: 'Suppress', value: 'SUPPRESS' },
  ];

  const addressRequiredValidation = (validation, error) => {
    return validation.when('deliveryTypeCode', {
      is: 'MAIL',
      then: validation.required(error),
    });
  };

  const validateEmails = (value) => {
    if (!value) return true;
  
    const emailList = value.split(';').filter(email => email.trim() !== '');
  
    return emailList.every(email => email && yup.string().email().isValidSync(email.trim()));
  };

  let validationSchema = yup.object().shape({
    firstName: string().nullable().matches(Regex.ALPHA_NUM_SPACE_SPECIAL_CHARACTER, Validation.ALPHA_NUM_SPECIAL_CHARACTER).max(19, Validation.MAX_CHAR_NINETEEN),
    lastName: string().nullable().matches(Regex.ALPHA_NUM_SPACE_SPECIAL_CHARACTER, Validation.ALPHA_NUM_SPECIAL_CHARACTER).max(19, Validation.MAX_CHAR_NINETEEN),
    deliveryTypeCode: string().nullable().required(Validation.DELIVERY_TYPE_REQUIRED),
    email: string()
      .nullable()
      .test('valid-emails', Validation.MULTIPLE_VALID_EMAILS_SEMICOLON, validateEmails)
      .max(255, Validation.MAX_CHAR_TWO_FIFTY_FIVE)
      .when('deliveryTypeCode', {
        is: 'EMAIL',
        then: yup.string().nullable().required(Validation.EMAIL_REQUIRED).test('valid-emails', Validation.MULTIPLE_VALID_EMAILS_SEMICOLON, validateEmails).max(255, Validation.MAX_CHAR_TWO_FIFTY_FIVE),
      }),
    businessPhone: string().nullable().mobile(Validation.MOBILE_LANDLINE, Regex.MOBILE_LANDLINE),
    mobilePhone: string().nullable().mobile(Validation.MOBILE, Regex.MOBILE),
    businessFax: string().nullable().mobile(Validation.MOBILE_LANDLINE, Regex.MOBILE_LANDLINE),
  });

  const modifiedAddressValidationSchema = addressValidationSchema.shape({
    line1: addressValidationSchema.fields.addressLine1,
    line2: addressRequiredValidation(addressValidationSchema.fields.addressLine2.optional(), Validation.ADDRESS_REQUIRED),
    city: addressRequiredValidation(addressValidationSchema.fields.city.optional(), Validation.CITY_REQUIRED),
    state: addressRequiredValidation(addressValidationSchema.fields.state.optional(), Validation.STATE_REQUIRED),
    zipCode: addressRequiredValidation(addressValidationSchema.fields.postcode.optional(), Validation.POSTCODE_REQUIRED),
  });

  validationSchema = validationSchema.concat(modifiedAddressValidationSchema);

  const {
    firstName,
    lastName,
    email,
    deliveryTypeCode,
    line1,
    line2,
    city,
    state,
    zipCode,
    businessPhone,
    mobilePhone,
    businessFax,
    addressTypeCode,
    editable,
    salutationCode,
  } = address;
  const defaultValues = useMemo(() => (address ? {
    firstName,
    lastName,
    email,
    deliveryTypeCode,
    line1,
    line2,
    city,
    state: state?.split(';')?.[0],
    zipCode,
    businessPhone,
    mobilePhone,
    businessFax,
    addressTypeCode,
    salutationCode,
  } : {}), [address]);

  const getDeliveryTypeOptions = () => {
    return deliveryTypeCode === 'EMAIL' || (deliveryTypeCode === suppressTypeCode && email) ?
      deliveryTypes.filter(item => item.value !== 'MAIL') :
      deliveryTypeCode === suppressTypeCode ?
        deliveryTypes :
        deliveryTypes.filter(item => item.value !== suppressTypeCode);
  };

  const [canEdit, setCanEdit] = useState(false);

  const handleStateChange = ({ isValid }) => {
    onStateChange({ isValid: isValid && !readOnly && deliveryTypeCode !== suppressTypeCode });
  };

  useEffect(() => {
    const [canEditAddress] = editable.split(';');
    setCanEdit(canEditAddress === 'Y' && !readOnly && deliveryTypeCode !== suppressTypeCode);
  }, [editable, deliveryTypeCode, readOnly]);

  return <>
    <div className="address">
      <h2 className="address__title">{addressTypeTitle[addressTypeCode] || title}</h2>
      <Form
        id={formId}
        schema={validationSchema}
        defaultValues={defaultValues}
        onSubmit={(values) => canEdit && onSubmit && onSubmit(values)}
        onStateChange={handleStateChange}
        triggerValidation={canEdit}
        HTMLFormProps={{ noValidate: true }}
      >
        <div className="address__fields">
          <Form.DropdownSearch label={'Salutation'} name={'salutationCode'} data={salutations} disabled={!canEdit} placeholder={'Please select...'} />
          <Form.Input label={'First Name'} className={'address__col1'} name={'firstName'} disabled={!canEdit} />
          <Form.Input label={'Last Name'} name={'lastName'} disabled={!canEdit} />
          {hasDeliveryType[addressType] &&
            <Form.DropdownSearch label={'Delivery Type'} name={'deliveryTypeCode'} data={getDeliveryTypeOptions()} disabled={!canEdit} placeholder={'Select Delivery Type'} />}
          <Form.Input label={'Email Address'} inputType={InputType.email} name={'email'} className={'address__field100 address__groupstart'} disabled={!canEdit} />
          <Form.Input label={'Address'} aria-label={'Address Line 1'} name={'line2'} className={'address__field100'} disabled={!canEdit} />
          <Form.Input label={'Company, unit, suite, etc.'} name={'line1'} className={'address__field100'} disabled={!canEdit} />
          <Form.Input label={'Town/City'} name={'city'} disabled={!canEdit} />
          <Form.DropdownSearch label={'State'} name={'state'} type={'dropdown'} data={AUSSIE_STATES} disabled={!canEdit} placeholder={'Select State'} />
          <Form.Input label={'Postcode'} name={'zipCode'} className={'address__field25'} disabled={!canEdit} />
          <Form.Input label={'Phone Number'} name={'businessPhone'} className={'address__col1'} disabled={!canEdit} />
          <Form.Input label={'Mobile Number'} name={'mobilePhone'} disabled={!canEdit} />
          <Form.Input label={'Fax Number'} name={'businessFax'} disabled={!canEdit} />
        </div>
      </Form>
    </div>
  </>;
};

export default AddressForm;
